import { Check, ChevronDown, CircleCheck, LoaderCircle, Search, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  LOGO_DEV_TOKEN,
  LOGO_RIGHTS_NOTICE,
  LogoImageError,
  type BrandCandidate,
  fetchLogoDevSvg,
  logoDevUrl,
  normalizeDomain,
  searchBrandCandidates
} from "./logo-image";

type Props = {
  onUse: (svg: string) => void;
};

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Buscar a logo da empresa pelo NOME da marca (2026-10-16), num bloco que
 * nasce FECHADO. Digitar o nome lista as marcas candidatas da base do
 * Logo.dev (`searchBrandCandidates`), cada uma com a logo; um domínio
 * digitado também vale como candidato. Pra USAR, exige a confirmação de
 * direitos (logo de terceiro é marca registrada — a mesma regra de
 * `web_api/infrastructure/logo_providers.py`): clicar sem marcar avisa em
 * vez de não fazer nada. A imagem é baixada e convertida no SVG que o
 * cadastro guarda. Nada é gravado aqui: a logo entra no formulário e vale
 * no "Salvar alterações", como o arquivo. Sem a chave pública
 * (`VITE_LOGO_DEV_TOKEN`), o bloco não aparece.
 */
export function LogoDevSearch({ onUse }: Props) {
  const [input, setInput] = useState("");
  const [candidates, setCandidates] = useState<BrandCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState<BrandCandidate | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [rightsWarning, setRightsWarning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const loadRef = useRef(0);

  useEffect(() => {
    const query = input.trim();
    const load = ++loadRef.current;
    if (query.length < 2) {
      setCandidates([]);
      setSearching(false);
      setSearched(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearching(true);
      setError(null);
      const typedDomain = query.includes(".") ? normalizeDomain(query) : null;
      let found: BrandCandidate[] = [];
      try {
        found = await searchBrandCandidates(query, controller.signal);
      } catch (cause) {
        if (controller.signal.aborted || load !== loadRef.current) return;
        if (!typedDomain) {
          setError(cause instanceof LogoImageError ? cause.message : "Não foi possível buscar marcas agora.");
        }
      }
      if (load !== loadRef.current) return;
      if (typedDomain && !found.some(item => item.domain === typedDomain)) {
        found = [{ name: typedDomain, domain: typedDomain }, ...found];
      }
      setCandidates(found);
      setSearched(true);
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [input]);

  if (!LOGO_DEV_TOKEN) return null;
  const token = LOGO_DEV_TOKEN;

  const choose = (candidate: BrandCandidate) => {
    setSelected(candidate);
    setPreviewFailed(false);
    setRightsConfirmed(false);
    setRightsWarning(false);
    setError(null);
    setDone(null);
  };

  const use = async () => {
    if (!selected) return;
    if (!rightsConfirmed) {
      setRightsWarning(true);
      return;
    }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      onUse(await fetchLogoDevSvg(selected.domain, token));
      setDone("Logo atualizada. Clique em “Salvar alterações” para gravar.");
    } catch (cause) {
      setError(cause instanceof LogoImageError ? cause.message : "Não foi possível usar essa logo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="logo-dev-search">
      <summary className="logo-dev-search-head">
        <Search size={16} aria-hidden="true" />
        <span>
          <strong>Buscar a logo pelo nome</strong>
          <small>Encontre a logo da empresa sem precisar de arquivo.</small>
        </span>
        <ChevronDown className="logo-dev-chevron" size={16} aria-hidden="true" />
      </summary>

      <div className="logo-dev-search-body">
        <label className="form-field logo-dev-domain">
          <span className="sr-only">Buscar empresa pelo nome</span>
          <span className="logo-dev-input">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              placeholder="Qual empresa você procura?"
              value={input}
              onChange={event => setInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter") event.preventDefault();
              }}
            />
            {searching && <LoaderCircle className="spin" size={15} aria-label="Buscando" />}
          </span>
        </label>

        {candidates.length > 0 && (
          <ul className="logo-dev-candidates" aria-label="Marcas encontradas">
            {candidates.map(candidate => (
              <li key={candidate.domain}>
                <button
                  type="button"
                  className={`logo-dev-candidate${selected?.domain === candidate.domain ? " is-selected" : ""}`}
                  aria-pressed={selected?.domain === candidate.domain}
                  onClick={() => choose(candidate)}
                >
                  <span className="logo-dev-candidate-mark">
                    <img
                      src={logoDevUrl(candidate.domain, token)}
                      alt=""
                      loading="lazy"
                      onError={event => {
                        event.currentTarget.style.visibility = "hidden";
                      }}
                    />
                  </span>
                  <span className="logo-dev-candidate-text">
                    <strong>{candidate.name}</strong>
                    <small>{candidate.domain}</small>
                  </span>
                  {selected?.domain === candidate.domain && (
                    <Check className="logo-dev-candidate-check" size={16} aria-hidden="true" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {searched && !searching && candidates.length === 0 && !error && (
          <small className="logo-dev-empty">Nenhuma empresa encontrada. Tente outro nome ou o site da empresa.</small>
        )}

        {selected && (
          <div className="logo-dev-result">
            <div className="logo-dev-preview">
              {previewFailed ? (
                <small>Não há logo disponível para {selected.name}.</small>
              ) : (
                <img
                  src={logoDevUrl(selected.domain, token)}
                  alt={`Logo encontrada para ${selected.name}`}
                  onError={() => setPreviewFailed(true)}
                />
              )}
            </div>
            {!previewFailed && (
              <div className="logo-dev-actions">
                <div className="logo-dev-selected">
                  <strong>{selected.name}</strong>
                  <small>{selected.domain}</small>
                </div>
                <label className={`logo-dev-rights${rightsWarning && !rightsConfirmed ? " is-warning" : ""}`}>
                  <input
                    type="checkbox"
                    checked={rightsConfirmed}
                    onChange={event => {
                      setRightsConfirmed(event.target.checked);
                      if (event.target.checked) setRightsWarning(false);
                    }}
                  />
                  <span>{LOGO_RIGHTS_NOTICE}</span>
                </label>
                {rightsWarning && !rightsConfirmed && (
                  <p className="logo-dev-warning" role="alert">
                    <TriangleAlert size={15} aria-hidden="true" />
                    Marque a confirmação acima para usar esta logo.
                  </p>
                )}
                <div className="logo-dev-buttons">
                  <button type="button" className="primary-button" disabled={busy} onClick={() => void use()}>
                    {busy && <LoaderCircle className="spin" size={15} />}
                    Usar esta logo
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {error && <p className="form-error" role="alert">{error}</p>}
        {done && (
          <p className="logo-dev-done" role="status">
            <CircleCheck size={15} aria-hidden="true" />
            {done}
          </p>
        )}
      </div>
    </details>
  );
}
